/**
 * The auth adapter and the step-up dialog.
 *
 * A4's `lib/api` handles transport and calls out to whatever adapter is registered here.
 * This provider is that adapter: it holds the step-up ids the session currently has, opens
 * the dialog when the API asks for one, and routes a dead or locked session to the right
 * screen without losing where the operator was.
 *
 * Mount it once, above the router outlet.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { StepUp, StepUpTier } from '@elutide/shared';
import { setAuthAdapter } from '@/lib/api';
import {
  beginStepUp,
  completeStepUpWithPasskey,
  completeStepUpWithPin,
  loginErrorMessage,
} from './api';
import type { StepUpCeremony } from './api';
import { PinPad } from './PinPad';
import './auth.css';

/** Held step-ups. The server is the authority on freshness; this is only a fast path. */
interface HeldStepUp {
  id: string;
  tier: StepUpTier;
  expiresAt: number;
}

interface PendingRequest {
  tier: 2 | 3;
  resolve: (id: string | null) => void;
}

export interface AuthContextValue {
  /** Ask for a step-up. Resolves with the id, or null when the operator backs out. */
  requestStepUp: (tier: StepUpTier) => Promise<string | null>;
  currentStepUpId: (tier: StepUpTier) => string | null;
  /** Drop every held step-up. Called on logout and on lock. */
  forgetStepUps: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  requestStepUp: async () => null,
  currentStepUpId: () => null,
  forgetStepUps: () => undefined,
});

export function useAuth(): AuthContextValue {
  return useContext(AuthContext);
}

/** A held step-up is only offered while it has a comfortable margin left. */
const FRESHNESS_MARGIN_MS = 20_000;

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const held = useRef<HeldStepUp[]>([]);
  const [pending, setPending] = useState<PendingRequest | null>(null);

  const currentStepUpId = useCallback((tier: StepUpTier): string | null => {
    if (tier === 0) return null;
    const now = Date.now();
    held.current = held.current.filter((s) => s.expiresAt > now);
    // A tier 3 step-up satisfies a tier 2 route; the reverse is never true.
    const match = held.current.find((s) => s.tier >= tier && s.expiresAt - FRESHNESS_MARGIN_MS > now);
    return match?.id ?? null;
  }, []);

  const remember = useCallback((stepUp: StepUp) => {
    held.current = [
      ...held.current.filter((s) => s.id !== stepUp.step_up_id),
      { id: stepUp.step_up_id, tier: stepUp.tier, expiresAt: Date.parse(stepUp.expires_at) },
    ];
  }, []);

  const forgetStepUps = useCallback(() => {
    held.current = [];
  }, []);

  const requestStepUp = useCallback(
    (tier: StepUpTier): Promise<string | null> => {
      if (tier === 0) return Promise.resolve(null);
      const existing = currentStepUpId(tier);
      if (existing) return Promise.resolve(existing);
      return new Promise<string | null>((resolve) => {
        setPending({ tier: tier === 3 ? 3 : 2, resolve });
      });
    },
    [currentStepUpId],
  );

  useEffect(() => {
    setAuthAdapter({
      currentStepUpId,
      requestStepUp,
      onUnauthenticated: () => {
        forgetStepUps();
        // A full navigation, not a router push: the session is gone, so throwing away the
        // in-memory query cache is the point.
        if (!window.location.pathname.startsWith('/login')) {
          window.location.assign(`/login?next=${encodeURIComponent(window.location.pathname)}`);
        }
      },
      onSessionLocked: () => {
        forgetStepUps();
        if (!window.location.pathname.startsWith('/lock')) {
          window.location.assign(`/lock?next=${encodeURIComponent(window.location.pathname)}`);
        }
      },
    });
  }, [currentStepUpId, requestStepUp, forgetStepUps]);

  const value = useMemo<AuthContextValue>(
    () => ({ requestStepUp, currentStepUpId, forgetStepUps }),
    [requestStepUp, currentStepUpId, forgetStepUps],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
      {pending ? (
        <StepUpDialog
          tier={pending.tier}
          onDone={(stepUp) => {
            if (stepUp) {
              remember(stepUp);
              void queryClient.invalidateQueries({ queryKey: ['session'] });
            }
            pending.resolve(stepUp?.step_up_id ?? null);
            setPending(null);
          }}
        />
      ) : null}
    </AuthContext.Provider>
  );
}

function StepUpDialog({ tier, onDone }: { tier: 2 | 3; onDone: (stepUp: StepUp | null) => void }) {
  const [ceremony, setCeremony] = useState<StepUpCeremony | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPin, setShowPin] = useState(false);
  const [pinReset, setPinReset] = useState(0);
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    let cancelled = false;
    beginStepUp(tier)
      .then((c) => {
        if (!cancelled) setCeremony(c);
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(loginErrorMessage(err));
      });
    return () => {
      cancelled = true;
    };
  }, [tier]);

  useEffect(() => {
    dialogRef.current?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDone(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDone]);

  async function withPasskey(): Promise<void> {
    if (!ceremony) return;
    setBusy(true);
    setError(null);
    try {
      onDone(await completeStepUpWithPasskey(ceremony));
    } catch (err) {
      setError(loginErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  // PIN-only mode: the server started no WebAuthn ceremony and the PIN carries the tier.
  const pinOnly = ceremony?.method === 'pin';

  async function withPin(pin: string): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      onDone(await completeStepUpWithPin(pin, pinOnly ? tier : undefined));
    } catch (err) {
      setError(loginErrorMessage(err));
      setPinReset((n) => n + 1);
    } finally {
      setBusy(false);
    }
  }

  const pinAllowed = pinOnly || (tier === 2 && (ceremony?.pin_allowed ?? false));

  return (
    <div className="a-scrim" role="presentation">
      <div
        className="a-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stepup-title"
        tabIndex={-1}
        ref={dialogRef}
      >
        <h2 className="a-dialog__title" id="stepup-title">
          Confirm it is you
        </h2>
        <p className="a-dialog__body">
          {pinOnly
            ? tier === 3
              ? 'This is a production control. Enter your PIN to go ahead, however recently you signed in.'
              : 'This change reaches a customer or the carrier. Enter your PIN to go ahead.'
            : tier === 3
              ? 'This is a production control. It needs a passkey every time, however recently you signed in.'
              : 'This action moves money or reaches a customer. Confirm with your passkey, or your PIN on this device.'}
        </p>

        {error ? (
          <p className="a-error" role="alert">
            {error}
          </p>
        ) : null}

        {pinOnly ? (
          <>
            <PinPad
              disabled={busy}
              resetToken={pinReset}
              onSubmit={(pin) => void withPin(pin)}
              label="Enter your PIN"
            />
            <div className="a-dialog__actions">
              <button type="button" className="a-btn a-btn--quiet" onClick={() => onDone(null)} disabled={busy}>
                Cancel
              </button>
            </div>
          </>
        ) : !ceremony && !error ? (
          <div className="a-dialog__actions">
            <p className="a-auth__hint">One moment.</p>
            <button type="button" className="a-btn a-btn--quiet" onClick={() => onDone(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        ) : showPin && pinAllowed ? (
          <PinPad
            disabled={busy}
            resetToken={pinReset}
            onSubmit={(pin) => void withPin(pin)}
            label="Enter your PIN"
          />
        ) : (
          <div className="a-dialog__actions">
            <button
              type="button"
              className="a-btn a-btn--primary"
              disabled={busy || !ceremony}
              onClick={() => void withPasskey()}
            >
              {busy ? 'Waiting for the device' : 'Use passkey'}
            </button>
            {pinAllowed ? (
              <button type="button" className="a-btn" onClick={() => setShowPin(true)} disabled={busy}>
                Use PIN instead
              </button>
            ) : null}
            <button type="button" className="a-btn a-btn--quiet" onClick={() => onDone(null)} disabled={busy}>
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
